import React, { useState, useEffect, useRef } from "react";
import { Button } from "@components/library";
import { useDispatch } from "react-redux";
import axios from "@helpers/axios/private.axios";
import { post2FA } from "@actions/user";
import Qr from "@assets/images/QR.png";
import GoogleAuthIcon from "@assets/images/icons/google-auth.svg";
import MicrosoftAuthIcon from "@assets/images/icons/microsoft-auth.svg";
import AuthyAuthIcon from "@assets/images/icons/authy-auth.svg";
import DuoAuthIcon from "@assets/images/icons/duo-auth.svg";
import { CopyToClipboard } from "react-copy-to-clipboard/src";
import { useTranslation } from "@helpers/translate";
import "./InlineTwoFactor.sass";
import downloadIcon from "@assets/images/icons/inbox-download.svg"
import toRightIcom from "@assets/images/icons/toright-arrow-icon.svg"
const Step1SelectApp = ({ onNext }) => {
  const { __ } = useTranslation();
  const [selectedApp, setSelectedApp] = useState(null);

  const apps = [
    { id: "google", name: __('settings.google_auth'), icon: GoogleAuthIcon },
    { id: "microsoft", name: __('settings.google_auth') === 'Google Authenticator' ? 'Microsoft Authenticator' : __('settings.google_auth'), icon: MicrosoftAuthIcon },
    { id: "authy", name: __('settings.authy') || 'Authy', icon: AuthyAuthIcon },
    { id: "duo", name: __('settings.duo') || 'Duo Mobile', icon: DuoAuthIcon },
  ];

  return (
    <div className="two-factor-modal">
      <h2 className="two-factor-modal__title">{__('settings.2fa_enable_title')}</h2>
      <p className="two-factor-modal__description">{__('settings.2fa_enable_description')}</p>
      <div className="two-factor-modal__apps">
        {apps.map((app) => (
          <div
            key={app.id}
            className={`two-factor-modal__app ${selectedApp === app.id ? "two-factor-modal__app--active" : ""}`}
            onClick={() => setSelectedApp(app.id)}
          >
            <div className="two-factor-modal__app-icon">
              <img src={app.icon || "/placeholder.svg"} alt={`${app.name} icon`} />
            </div>
            <div className="two-factor-modal__app-name">{app.name}</div>
            <img src={downloadIcon} alt="download" />
          </div>
        ))}
      </div>
      <div className="two-factor-modal__footer">
        <Button type="button" color="orange" size="middle" onClick={onNext} disabled={!selectedApp} className={`two-factor-modal__btn ${selectedApp ? "two-factor-modal__btn--active" : ""}`}>
          {__('common.next')}

          <img src={toRightIcom} alt="arrow"  />
        </Button>
        
      </div>
    </div>
  );
};

const Step2QRCode = ({ onNext, onQRLoaded }) => {
  const [qrData, setQrData] = useState({ qr: null, secret: '' });
  const [loading, setLoading] = useState(true);
  const [showCopiedPopup, setShowCopiedPopup] = useState(false);
  const { __ } = useTranslation();

  useEffect(() => {
    const fetchQR = async () => {
      setLoading(true);
      try {
        const resp = await axios.get("/users/2fa/init");
        const { qr, secret } = resp.data.data;
        setQrData({ qr, secret });
        if (onQRLoaded) onQRLoaded(secret);
      } catch (e) {
        setQrData({ qr: Qr, secret: "ERROR_LOADING_SECRET" });
      } finally {
        setLoading(false);
      }
    };
    fetchQR();
  }, [onQRLoaded]);

  const handleCopy = () => {
    setShowCopiedPopup(true);
    setTimeout(() => setShowCopiedPopup(false), 2000);
  };

  return (
    <div className="two-factor-modal">
      <h2 className="two-factor-modal__title">{__('settings.link_auth_app_title')}</h2>
      <p className="two-factor-modal__description">{__('settings.link_auth_app_description')}</p>
      <div className="two-factor-modal__qr-container">
        {loading ? <div className="two-factor-modal__qr-loading">Loading...</div> : <img src={qrData.qr} alt="QR Code" className="two-factor-modal__qr-code" />}
      </div>
      {!loading && (
        <div className="two-factor-modal__secret">
          <p className="two-factor-modal__secret-value">{qrData.secret}</p>
          <CopyToClipboard text={qrData.secret} onCopy={handleCopy}>
            <button type="button" className="two-factor-modal__secret-copy">{__('settings.copy_key')}</button>
          </CopyToClipboard>
        </div>
      )}
      <div className="two-factor-modal__footer">
        <Button type="button" color="orange" size="middle" onClick={onNext} disabled={loading} className="two-factor-modal__btn">{__('common.next')}  <img src={toRightIcom} alt="arrow"  /></Button>
      </div>
      {showCopiedPopup && (
        <div className="profile__copied-popup"><span>{__('common.copied')}</span></div>
      )}
    </div>
  );
};

const Step3EnterCode = ({ onSuccess, qrSecret, onCancel }) => {
  const dispatch = useDispatch();
  const { __ } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(Array(6).fill(""));
  const inputRefs = useRef([]);

  const isComplete = code.every((c) => /^\d$/.test(c));
  const codeString = code.join("");

  const handleChange = (index, e) => {
    const val = (e.target.value || "").replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[index] = val;
    setCode(next);
    if (val && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (code[index]) {
        const next = [...code]; next[index] = ""; setCode(next);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const next = [...code]; next[index - 1] = ""; setCode(next);
      }
    }
    if (e.key === "ArrowLeft" && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData("Text") || "").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = Array(6).fill("");
    pasted.split("").forEach((ch, i) => (next[i] = ch));
    setCode(next);
    const focusIndex = Math.min(pasted.length, 5);
    inputRefs.current[focusIndex]?.focus();
    e.preventDefault();
  };

  const handleConfirm = async () => {
    if (!isComplete) return;
    setLoading(true);
    try {
      await dispatch(post2FA({ data: { code: codeString }, secret: qrSecret }));
      onSuccess();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setCode(Array(6).fill(""));
    }
  };

  return (
    <div className="two-factor-modal">
      <h2 className="two-factor-modal__title">{__('settings.enter_code_title')}</h2>
      <p className="two-factor-modal__description">{__('settings.enter_2fa')}</p>
      <div className="two-factor-modal__form">
        <div className="two-factor-modal__code-container" onPaste={handlePaste}>
          {code.map((digit, idx) => (
            <input key={idx} ref={(el) => (inputRefs.current[idx] = el)} className="two-factor-modal__code-input" inputMode="numeric" pattern="\d*" maxLength={1} value={digit} onChange={(e) => handleChange(idx, e)} onKeyDown={(e) => handleKeyDown(idx, e)} />
          ))}
        </div>
        <div className="two-factor-modal__footer--third">
          <button type="button" disabled={loading} onClick={onCancel} className={`two-factor-modal__btn-cancel ${isComplete ? "two-factor-modal__btn-cancel--inactive" : "two-factor-modal__btn-cancel--active"}`}>{__('common.cancel')}</button>
          <button type="button" disabled={!isComplete || loading} onClick={handleConfirm} className={`two-factor-modal__btn-confirm ${isComplete ? "two-factor-modal__btn-confirm--active" : "two-factor-modal__btn-confirm--inactive"}`}>{__('common.confirm')}</button>
        </div>
      </div>
    </div>
  );
};

const InlineTwoFactor = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [qrSecret, setQrSecret] = useState(null);

  const handleClose = () => {
    setStep(1);
    setQrSecret(null);
    if (onClose) onClose();
  };

  const handleSuccess = () => {
    setStep(1);
    setQrSecret(null);
    if (onSuccess) onSuccess();
  };

  return (
    <div className="inline-two-factor">
      {step === 1 && <Step1SelectApp onNext={() => setStep(2)} />}
      {step === 2 && <Step2QRCode onNext={() => setStep(3)} onQRLoaded={setQrSecret} />}
      {step === 3 && <Step3EnterCode onSuccess={handleSuccess} qrSecret={qrSecret} onCancel={handleClose} />}
      
    </div>
  );
};

export default InlineTwoFactor;
